
/*
 * PureWay Wellness — iCharge Gallery Autoplay
 * Version: 2.0 — 15 October 2026
 *
 * Image duration: 4 seconds
 * Shopify-hosted videos: play until completion
 * Continuous slideshow: enabled
 * Manual navigation: supported
 *
 * IMPORTANT:
 * This script activates only when the page contains:
 * body.pw-icharge-gallery-autoplay
 *
 * Do not modify Shopify's shared slideshow.js.
 */

(() => {
  'use strict';

  const IMAGE_DURATION = 4000;

  const GALLERY_SELECTOR =
    'body.pw-icharge-gallery-autoplay media-gallery.media-gallery--carousel slideshow-component';

  const initialized = new WeakSet();

  function initializeGallery(slideshow) {
    if (initialized.has(slideshow)) return;

    const slides = Array.from(
      slideshow.querySelectorAll(
        'slideshow-slides > slideshow-slide'
      )
    );

    if (slides.length < 2) return;

    initialized.add(slideshow);

    let timer = null;
    let activeVideo = null;
    let activeVideoEndedHandler = null;
    let generation = 0;
    let previousIndex = -1;

    // Disable any built-in slideshow autoplay so
    // our image/video timing does not conflict.
    slideshow.removeAttribute('autoplay');

    // Keep continuous looping enabled.
    slideshow.setAttribute('infinite', '');

    function getActiveIndex() {
      const index = slides.findIndex(
        (slide) =>
          slide.getAttribute('aria-hidden') === 'false'
      );

      return index >= 0 ? index : 0;
    }

    function clearTimer() {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function stopCurrentVideo() {
      if (!activeVideo) return;

      if (activeVideoEndedHandler) {
        activeVideo.removeEventListener(
          'ended',
          activeVideoEndedHandler
        );
      }

      try {
        activeVideo.pause();
      } catch (error) {
        // Video may already be unloaded.
      }

      activeVideo = null;
      activeVideoEndedHandler = null;
    }

    function cancelCurrentPlayback() {
      generation += 1;
      clearTimer();
      stopCurrentVideo();
    }

    function advanceSlide() {
      if (document.hidden || !slideshow.isConnected) {
        return;
      }

      clearTimer();

      const currentIndex = getActiveIndex();
      const nextIndex = (currentIndex + 1) % slides.length;

      // Use the theme's existing slide navigation.
      if (typeof slideshow.select === 'function') {
        slideshow.select(nextIndex);
      } else if (typeof slideshow.next === 'function') {
        slideshow.next();
      }
    }

    function scheduleImage() {
      clearTimer();

      timer = window.setTimeout(() => {
        advanceSlide();
      }, IMAGE_DURATION);
    }

    async function playCurrentSlide() {
      cancelCurrentPlayback();

      if (document.hidden || !slideshow.isConnected) {
        return;
      }

      const currentGeneration = generation;
      const slide = slides[getActiveIndex()];

      if (!slide) return;

      const isShopifyVideo = slide.classList.contains(
        'product-media-container--video'
      );

      const isExternalVideo = slide.classList.contains(
        'product-media-container--external_video'
      );

      // Images advance after four seconds.
      if (!isShopifyVideo && !isExternalVideo) {
        scheduleImage();
        return;
      }

      // External YouTube/Vimeo videos require
      // their respective player APIs to detect completion.
      // Do not incorrectly skip them after four seconds.
      if (isExternalVideo) {
        return;
      }

      const deferredMedia =
        slide.querySelector('deferred-media');

      if (!deferredMedia) {
        return;
      }

      let video = deferredMedia.querySelector('video');

      // Shopify may load videos only when activated.
      if (!video) {
        const playButton = deferredMedia.querySelector(
          '[ref="deferredMediaPlayButton"]'
        );

        if (playButton) {
          playButton.click();
        }

        // Wait for the deferred video to be inserted.
        for (let attempt = 0; attempt < 40; attempt++) {
          if (currentGeneration !== generation) {
            return;
          }

          video = deferredMedia.querySelector('video');

          if (video) break;

          await new Promise((resolve) => {
            window.setTimeout(resolve, 100);
          });
        }
      }

      if (
        currentGeneration !== generation ||
        !video ||
        document.hidden
      ) {
        return;
      }

      activeVideo = video;

      // Ensure the video ends naturally.
      video.loop = false;

      // Muted playback is more widely permitted
      // by desktop and mobile browsers.
      video.muted = true;
      video.playsInline = true;

      try {
        video.currentTime = 0;
      } catch (error) {
        // Metadata may not yet be available.
      }

      activeVideoEndedHandler = () => {
        if (currentGeneration !== generation) {
          return;
        }

        advanceSlide();
      };

      video.addEventListener(
        'ended',
        activeVideoEndedHandler,
        { once: true }
      );

      try {
        await video.play();
      } catch (error) {
        // Browser may prevent automatic playback.
        // Leave the video visible for manual playback.
      }
    }

    function synchronizeSlides() {
      const index = getActiveIndex();

      if (index === previousIndex) {
        return;
      }

      previousIndex = index;
      playCurrentSlide();
    }

    // Detect changes caused by arrows,
    // thumbnails, swipes or automatic navigation.
    const observer = new MutationObserver(
      synchronizeSlides
    );

    slides.forEach((slide) => {
      observer.observe(slide, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    });

    // Stop playback when the browser tab is hidden.
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          cancelCurrentPlayback();
        } else {
          previousIndex = -1;
          synchronizeSlides();
        }
      }
    );

    // Start with the currently selected slide.
    synchronizeSlides();
  }

  function initialize() {
    document
      .querySelectorAll(GALLERY_SELECTOR)
      .forEach(initializeGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }

  // Support Shopify Theme Editor section reloads.
  document.addEventListener(
    'shopify:section:load',
    initialize
  );
})();
